import { EnergyType, PriceFrequency, State } from '@energy-retailer/constants';
import {
  NavigationChevronDown,
  NavigationChevronUp,
} from '@energy-retailer/icons';
import { twMerge } from 'tailwind-merge';

import {
  formatProductPlanDefaultOffer,
  formatProductPlanPrice,
  formatProductPlanTariff,
  getPriceFrequencyText,
  getProductPlanEstimatedPrice,
} from '@/app/lib/utils';
import { getPlanComparisonText } from '@/app/lib/utils/get-plan-comparison-text';
import { getPlanDocsURL } from '@/app/lib/utils/get-plan-docs-url';

import { Button } from '../button';
import { Collapse } from '../collapse';
import { Divider } from '../divider';
import { ContentfulIcon } from '../icon';
import { TariffInformation } from '../product-plan-card/tariff-information';
import { TariffQuoteText } from '../product-plan-card/tariff-quote-text';
import { RichText } from '../rich-text';
import { Text } from '../text';
import { Tooltip } from '../tooltip';

import { PlanDetailItem } from './plan-detail-item';
import { cardVariants } from './style';
import { PlanTariffTooltip } from './tariff-tooltip';

import type { ProductPlanCardCampaignProps } from './index.types';
import type { CardVariants } from '../product-plan-card/style';
import type { CmsPlanSchema } from '@/app/lib/hooks/use-plans-cms';
import type { Document } from '@contentful/rich-text-types';
import type { FeedInRatesSchema } from '@energy-retailer/schema';

const ProductPlanCardCampaign = ({
  plan,
  hasAddress,
  priceFrequency = PriceFrequency.Yearly,
  handleAddAddress,
}: ProductPlanCardCampaignProps) => {
  const { product } = plan;

  const { controlledLoad, feedInRates } = plan as CmsPlanSchema & {
    controlledLoad: boolean;
    feedInRates?: FeedInRatesSchema;
  };

  const price = getProductPlanEstimatedPrice({
    estimates: plan.estimates,
    priceFrequency,
  });

  const priceFrequencyText = getPriceFrequencyText({
    priceFrequency,
  });

  return (
    <div
      className={twMerge(
        cardVariants({
          planType: plan.product.productType as CardVariants['planType'],
        }),
        'h-full flex-1'
      )}
    >
      <div className="flex items-center gap-b">
        <ContentfulIcon
          id={product.icon?.id ?? ''}
          className="h-icon-md w-icon-md rounded-rad-a bg-gray-50 p-a text-product-plan-card-accent lg:h-icon-lg lg:w-icon-lg"
        />
        <div className="flex flex-col gap-a">
          <Text
            variant="h3"
            as="h3"
            className="text-product-plan-card-accent"
          >
            {product.title}
          </Text>
          {product.tagline ? (
            <Text
              variant="body3"
              className="text-gray-500"
            >
              {product.tagline}
            </Text>
          ) : null}
        </div>
      </div>
      <div>
        <Text
          variant="body2"
          weight="semi"
        >
          {`${plan.customerType} ${plan.energyType}`}
        </Text>
      </div>
      {product.callOutFeature?.description ? (
        <div className="flex items-center gap-b rounded-rad-c border border-info p-c">
          <ContentfulIcon
            id={product.callOutFeature.icon?.id ?? 'tag'}
            className="h-icon w-icon"
          />

          <RichText
            links={product.callOutFeature.description.links}
            document={product.callOutFeature.description.json as Document}
            bodyVariant="body3"
          />
        </div>
      ) : null}
      <div className="flex flex-col gap-c">
        {product.featuresCollection?.items.map((feature) => {
          if (!feature?.description) {
            return null;
          }

          return (
            <div
              key={feature._id}
              className="flex items-center gap-b"
            >
              <ContentfulIcon
                id={feature.icon?.id ?? ''}
                className="h-icon w-icon min-w-icon text-product-plan-card-accent md:h-icon-xs md:w-icon-xs md:min-w-icon-xs"
              />
              <div>
                <RichText
                  links={feature.description.links}
                  document={feature.description.json as Document}
                  bodyVariant="body2"
                />
              </div>
            </div>
          );
        })}
      </div>
      {hasAddress ? (
        <>
          <PlanDetailItem
            subHeading={
              <Text
                variant="body2"
                as="p"
              >
                * estimated price per {priceFrequencyText} (incl GST)
              </Text>
            }
          >
            {formatProductPlanPrice(Number(price) || undefined)}<sup>*</sup> per{' '}
            {priceFrequencyText}
          </PlanDetailItem>
          {plan.energyType === EnergyType.Electricity ? (
            <PlanDetailItem
              subHeading={
                <div className="flex flex-col gap-c">
                  <div>
                    <a
                      className="text-info underline decoration-info"
                      data-tooltip-id={String(plan.offerTooltip.id)}
                      aria-describedby={String(plan.offerTooltip.id)}
                    >
                      the{' '}
                      {plan.state === State.VIC
                        ? 'Victorian Default Offer*'
                        : 'reference price*'}{' '}
                    </a>
                    <Tooltip id={String(plan.offerTooltip.id)}>
                      {plan.offerTooltip.text}
                    </Tooltip>
                  </div>
                  <PlanTariffTooltip plan={plan} />
                </div>
              }
            >
              {formatProductPlanDefaultOffer(Number(plan.comparison))}{' '}
              {getPlanComparisonText(Number(plan.comparison)).toLowerCase()}
            </PlanDetailItem>
          ) : (
            <PlanTariffTooltip plan={plan} />
          )}

          {plan.energyType === EnergyType.Electricity ? (
            <PlanDetailItem subHeading="Solar Feed-in Tariff">
              {formatProductPlanTariff({
                tariff: Number(plan.feedInRates?.default) || 0,
              })}
            </PlanDetailItem>
          ) : null}
          <PlanDetailItem subHeading="super off-peak tariff">
            {formatProductPlanTariff({
              tariff: plan.rates.superOffPeak
                ? Number(plan.rates.superOffPeak)
                : undefined,
              showZero: true,
            })}
          </PlanDetailItem>
          <div className="mt-auto">
            <Collapse
              openIcon={<NavigationChevronUp />}
              closeIcon={<NavigationChevronDown />}
              variant="minimal"
              title="Plan details: Pricing, rates & tariffs"
            >
              <div className="flex flex-col gap-b py-b xl:flex-row xl:gap-e">
                <div className="flex grow flex-col gap-b">
                  <Text
                    variant="h4"
                    as="h4"
                  >
                    {plan.energyType} rates and pricing
                  </Text>
                  <TariffQuoteText
                    docsHref={getPlanDocsURL(plan)}
                    hasAddress={hasAddress}
                    state={plan.state}
                  />
                  <TariffInformation
                    planName={plan.product.title ?? ''}
                    energyType={plan.energyType}
                    includeSolarFeedTariff
                    controlledLoad={controlledLoad as boolean}
                    rates={plan.rates}
                    feedInRates={feedInRates as FeedInRatesSchema}
                  />
                </div>
                <Divider className="xl:hidden" />
                <div className="flex flex-col gap-b">
                  <Text
                    variant="h4"
                    as="h4"
                  >
                    Offer Details
                  </Text>
                  {plan.product.offerDetails ? (
                    <RichText
                      bodySpacing="none"
                      links={plan.product.offerDetails.links}
                      document={plan.product.offerDetails.json as Document}
                      bodyVariant="body2"
                    />
                  ) : null}
                </div>
              </div>
            </Collapse>
          </div>
        </>
      ) : (
        <Button
          variant="secondary"
          className="mt-auto w-full"
          onClick={handleAddAddress}
        >
          Add Address
        </Button>
      )}
    </div>
  );
};

export { ProductPlanCardCampaign };
